import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import B2BChatPanel from './B2BChatPanel';
import ChatModal from '../components/ChatModal';
import ClientChatWidget from '../components/ClientChatWidget';
import {
  LayoutDashboard, Wrench, PlusCircle, LogOut, Building2,
  Package, Clock, CheckCircle, Truck, AlertTriangle,
  ChevronRight, Search, Smartphone, Hash, Camera,
  FileText, Send, ArrowLeft, RefreshCw, Menu, Lock,
  DollarSign, Printer, Download, MapPin, Mail as MailIcon,
  X, Upload, Image as ImageIcon, ChevronDown, ChevronUp,
  MessageSquare, FolderOpen, Bell, CreditCard
} from 'lucide-react';

import ChangePasswordModal from '../components/ChangePasswordModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = {
  DASHBOARD: 'dashboard',
  NEW_REPAIR: 'new_repair',
  REPAIRS: 'repairs',
  REPAIR_DETAIL: 'repair_detail',
  DOCUMENTS: 'documents',
  DEVIS: 'devis',
  QUOTE_REQUEST: 'quote_request',
  CHAT: 'chat',
};

const STATUS_CONFIG = {
  'En attente de réception': { label: 'Att. réception', color: 'text-gray-400', bg: 'bg-gray-500/10 border-gray-500/30', icon: Package },
  'En attente de récupération': { label: 'Att. récupération', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30', icon: Truck },
  'Reçu': { label: 'Reçu', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30', icon: Package },
  'En Cours': { label: 'En Cours', color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30', icon: Clock },
  'Attente Pièces': { label: 'Att. Pièces', color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/30', icon: AlertTriangle },
  'Terminé': { label: 'Terminé', color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/30', icon: CheckCircle },
  'Livré': { label: 'Livré', color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/30', icon: Truck },
  'Refusé': { label: 'Refusé', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', icon: AlertTriangle },
};

// Les valeurs de statut backend (clés) ne sont JAMAIS traduites ; STATUS_LABEL_KEYS les
// mappe vers des clés i18n pour l'AFFICHAGE uniquement, avec repli sur le libellé brut.
const STATUS_LABEL_KEYS = {
  'En attente de réception': 'b2bDashboard.status.awaitingReception',
  'En attente de récupération': 'b2bDashboard.status.awaitingPickup',
  'Reçu': 'b2bDashboard.status.received',
  'En Cours': 'b2bDashboard.status.inProgress',
  'Attente Pièces': 'b2bDashboard.status.awaitingParts',
  'Terminé': 'b2bDashboard.status.completed',
  'Livré': 'b2bDashboard.status.delivered',
  'Refusé': 'b2bDashboard.status.refused',
};
const statusLabel = (t, status, fallback) => {
  const k = STATUS_LABEL_KEYS[status];
  return k ? t(k) : (fallback != null ? fallback : status);
};

// Catalogue des pièces. `key` est un identifiant technique (jamais traduit) ;
// labelKey pointe vers le libellé i18n affiché à l'utilisateur.
const partsOptions = [
  { key: 'speaker', labelKey: 'b2bDashboard.parts.speaker' },
  { key: 'lcd', labelKey: 'b2bDashboard.parts.lcd' },
  { key: 'front_cam', labelKey: 'b2bDashboard.parts.frontCam' },
  { key: 'back_cam', labelKey: 'b2bDashboard.parts.backCam' },
  { key: 'microphone', labelKey: 'b2bDashboard.parts.microphone' },
  { key: 'fingerprint', labelKey: 'b2bDashboard.parts.fingerprint' },
  { key: 'home_button', labelKey: 'b2bDashboard.parts.homeButton' },
  { key: 'volume_button', labelKey: 'b2bDashboard.parts.volumeButton' },
  { key: 'power_button', labelKey: 'b2bDashboard.parts.powerButton' },
  { key: 'signal_carrier', labelKey: 'b2bDashboard.parts.signal' },
  { key: 'battery', labelKey: 'b2bDashboard.parts.battery' },
  { key: 'wifi_bt', labelKey: 'b2bDashboard.parts.wifiBt' },
  { key: 'ear_speaker', labelKey: 'b2bDashboard.parts.earSpeaker' },
  { key: 'charging_port', labelKey: 'b2bDashboard.parts.chargingPort' },
  { key: 'back_glass', labelKey: 'b2bDashboard.parts.backGlass' }
];
const partLabel = (t, key) => {
  const opt = partsOptions.find(o => o.key === key);
  return opt ? t(opt.labelKey) : key;
};

// Push notification setup helper
async function setupPushNotifications(api) {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    const reg = await navigator.serviceWorker.register('/sw.js');
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const res = await api.get('/push/vapid-key');
    const vapidKey = res.data.public_key;
    const urlBase64 = vapidKey.replace(/-/g, '+').replace(/_/g, '/');
    const rawKey = Uint8Array.from(atob(urlBase64.padEnd(urlBase64.length + (4 - urlBase64.length % 4) % 4, '=')), c => c.charCodeAt(0));

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: rawKey });
    }
    await api.post('/push/subscribe', { subscription: sub.toJSON() });
  } catch (e) { console.warn('Push setup:', e); }
}

const B2BDashboardPage = () => {
  const { api, logout } = useAuth();
  const { t, tError } = useLanguage();
  const [tab, setTab] = useState(TABS.DASHBOARD);
  const [dashboard, setDashboard] = useState(null);
  const [repairs, setRepairs] = useState([]);
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [mobileNav, setMobileNav] = useState(false);
  const [unreadChats, setUnreadChats] = useState(0);
  const [b2bDevis, setB2bDevis] = useState([]);
  const [b2bFactures, setB2bFactures] = useState([]);
  const [b2bDevisRequests, setB2bDevisRequests] = useState([]);
  const [showB2bRequestForm, setShowB2bRequestForm] = useState(false);
  const [b2bRequestForm, setB2bRequestForm] = useState({ titre: '', description: '', adresse: '', marque: '', modele: '' });
  const [submittingB2bRequest, setSubmittingB2bRequest] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try { const res = await api.get('/b2b/dashboard'); setDashboard(res.data); } catch (err) { console.error(err); }
  }, [api]);

  const fetchRepairs = useCallback(async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await api.get(`/b2b/repairs${params}`);
      setRepairs(res.data);
    } catch (err) { console.error(err); }
  }, [api, statusFilter]);

  const fetchUnread = useCallback(async () => {
    try { const res = await api.get('/b2b/chat/unread-total'); setUnreadChats(res.data.total); } catch {}
  }, [api]);

  const fetchB2bDevis = useCallback(async () => {
    try {
      const res = await api.get('/b2b/devis');
      setB2bDevis(res.data?.devis || []);
      setB2bFactures(res.data?.factures || []);
    } catch (err) { console.error('Error fetching B2B devis:', err); }
  }, [api]);

  const fetchB2bDevisRequests = useCallback(async () => {
    try {
      const res = await api.get('/b2b/devis-requests');
      setB2bDevisRequests(res.data || []);
    } catch (err) { console.error('Error fetching B2B devis requests:', err); }
  }, [api]);

  const submitB2bRequest = async () => {
    if (!b2bRequestForm.description.trim()) { alert(t('b2bDashboard.alertDescribeRequest')); return; }
    setSubmittingB2bRequest(true);
    try {
      await api.post('/b2b/devis-request', b2bRequestForm);
      setB2bRequestForm({ titre: '', description: '', adresse: '', marque: '', modele: '' });
      setShowB2bRequestForm(false);
      fetchB2bDevisRequests();
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('b2bDashboard.error'));
    } finally {
      setSubmittingB2bRequest(false);
    }
  };

  useEffect(() => {
    const init = async () => { setLoading(true); await Promise.all([fetchDashboard(), fetchRepairs(), fetchUnread(), fetchB2bDevis(), fetchB2bDevisRequests()]); setLoading(false); };
    init();
    const pollUnread = setInterval(fetchUnread, 15000);

    // Setup push notifications
    setupPushNotifications(api);

    return () => clearInterval(pollUnread);
  }, [fetchDashboard, fetchRepairs, fetchUnread]);

  const openRepairDetail = async (repair) => {
    try {
      const res = await api.get(`/b2b/repairs/${repair.ref}`);
      setSelectedRepair(res.data);
    } catch { setSelectedRepair(repair); }
    setTab(TABS.REPAIR_DETAIL);
  };

  const handleLogout = () => { logout(); window.location.href = '/login'; };

  const navItems = [
    { key: TABS.DASHBOARD, icon: LayoutDashboard, label: t('dash.title') },
    { key: TABS.NEW_REPAIR, icon: PlusCircle, label: t('rep.new') },
    { key: TABS.REPAIRS, icon: Wrench, label: t('client.repairs') },
    { key: TABS.DEVIS, icon: FileText, label: t('nav.devisDeplacement'), badge: b2bDevis.filter(d => d.status === 'en_attente').length || 0 },
    { key: TABS.QUOTE_REQUEST, icon: PlusCircle, label: t('b2bDashboard.nav.quoteRequest'), badge: b2bDevisRequests.filter(r => r.status === 'nouveau').length || 0 },
    { key: TABS.DOCUMENTS, icon: FolderOpen, label: t('b2bDashboard.nav.documents') },
    { key: TABS.CHAT, icon: MessageSquare, label: t('client.messages'), badge: unreadChats },
  ];

  const filtered = repairs.filter(r =>
    !search || r.ref?.toLowerCase().includes(search.toLowerCase()) ||
    r.brand?.toLowerCase().includes(search.toLowerCase()) ||
    r.model?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a1a' }}>
        <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: '#0a0a1a' }} data-testid="b2b-dashboard-page">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-black/80 backdrop-blur-xl border-r border-white/10 transform transition-transform lg:relative lg:translate-x-0 ${mobileNav ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-5 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-black text-purple-400 text-sm">R.E.Mobiles</div>
                <div className="text-xs text-gray-500">{t('login.businessSpace')}</div>
              </div>
            </div>
            {dashboard?.company && (
              <div className="mt-3 px-3 py-2 bg-white/5 rounded-lg">
                <p className="text-white text-sm font-semibold truncate" data-testid="b2b-company-name">{dashboard.company.company_name}</p>
                <p className="text-xs text-gray-400 truncate">{dashboard.company.email}</p>
              </div>
            )}
          </div>
          <nav className="flex-1 p-3 space-y-1">
            {navItems.map(item => {
              const Icon = item.icon;
              const active = tab === item.key || (item.key === TABS.REPAIRS && tab === TABS.REPAIR_DETAIL);
              return (
                <button key={item.key}
                  onClick={() => { setTab(item.key); setMobileNav(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${active ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                  data-testid={`b2b-nav-${item.key}`}>
                  <Icon className="w-4 h-4" />{item.label}
                  {item.badge > 0 && <span className="ml-auto bg-purple-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">{item.badge}</span>}
                </button>
              );
            })}
          </nav>
          <div className="p-3 border-t border-white/10 space-y-2">
            <button onClick={() => setShowChangePassword(true)}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-purple-300 hover:bg-purple-500/10 transition-colors"
              data-testid="b2b-change-password-btn">
              <Lock className="w-4 h-4" />{t('b2bDashboard.changePassword')}
            </button>
            <button onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors"
              data-testid="b2b-logout-btn">
              <LogOut className="w-4 h-4" />{t('nav.logout')}
            </button>
          </div>
        </div>
      </aside>
      {mobileNav && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setMobileNav(false)} />}

      <main className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-20 flex items-center gap-3 px-4 py-3 bg-black/80 backdrop-blur-xl border-b border-white/10">
          <button onClick={() => setMobileNav(true)} className="p-2 rounded-lg bg-white/5 text-white" data-testid="b2b-mobile-menu">
            <Menu className="w-5 h-5" />
          </button>
          <span className="text-white font-bold text-sm">{dashboard?.company?.company_name}</span>
        </div>
        <div className="p-4 lg:p-8 max-w-6xl mx-auto">
          {tab === TABS.DASHBOARD && <DashboardTab dashboard={dashboard} repairs={repairs} onViewRepair={openRepairDetail} onNavigate={setTab} />}
          {tab === TABS.NEW_REPAIR && <NewRepairTab api={api} onSuccess={() => { fetchDashboard(); fetchRepairs(); }} onViewRepair={openRepairDetail} />}
          {tab === TABS.REPAIRS && <RepairsListTab repairs={filtered} allRepairs={repairs} statusFilter={statusFilter} setStatusFilter={setStatusFilter} search={search} setSearch={setSearch} onViewRepair={openRepairDetail} />}
          {tab === TABS.REPAIR_DETAIL && selectedRepair && <RepairDetailTab repair={selectedRepair} api={api} onBack={() => setTab(TABS.REPAIRS)} onRefresh={() => openRepairDetail(selectedRepair)} />}
          {tab === TABS.DEVIS && <B2BDevisTab devis={b2bDevis} factures={b2bFactures} api={api} onRefresh={fetchB2bDevis} />}
          {tab === TABS.QUOTE_REQUEST && (
            <B2BQuoteRequestTab
              requests={b2bDevisRequests}
              showForm={showB2bRequestForm}
              setShowForm={setShowB2bRequestForm}
              form={b2bRequestForm}
              setForm={setB2bRequestForm}
              onSubmit={submitB2bRequest}
              submitting={submittingB2bRequest}
            />
          )}
          {tab === TABS.DOCUMENTS && <DocumentsTab api={api} />}
          {tab === TABS.CHAT && <B2BChatPanel api={api} repairs={repairs} />}
        </div>
      </main>

      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        accentColor="#a855f7"
      />
    </div>
  );
};

// ========== DASHBOARD TAB ==========
const DashboardTab = ({ dashboard, repairs, onViewRepair, onNavigate }) => {
  const { t, tError } = useLanguage();
  const stats = dashboard?.stats || {};
  const statCards = [
    { label: t('common.all'), value: stats.total || 0, icon: Wrench, color: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400' },
    { label: t('b2b.thisMonth'), value: stats.this_month || 0, icon: Package, color: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400' },
    { label: t('dash.inProgress'), value: stats.in_progress || 0, icon: Clock, color: 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30 text-yellow-400' },
    { label: t('b2b.waitingReception'), value: stats.pending || 0, icon: Package, color: 'from-gray-500/20 to-gray-600/10 border-gray-500/30 text-gray-400' },
    { label: t('dash.completed'), value: stats.completed || 0, icon: CheckCircle, color: 'from-green-500/20 to-green-600/10 border-green-500/30 text-green-400' },
  ];

  return (
    <div className="space-y-6" data-testid="b2b-dashboard-tab">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-white">{t('dash.title')}</h1>
        <p className="text-gray-400 text-sm mt-1">{t('b2b.welcome')}, {dashboard?.company?.manager_name}</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {statCards.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className={`bg-gradient-to-br ${s.color} border rounded-xl p-4`} data-testid={`b2b-stat-${i}`}>
              <Icon className="w-5 h-5 opacity-70 mb-2" />
              <p className="text-2xl font-black text-white">{s.value}</p>
              <p className="text-xs text-gray-400 mt-1">{s.label}</p>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button onClick={() => onNavigate(TABS.NEW_REPAIR)}
          className="flex items-center gap-3 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl hover:bg-purple-500/20 transition-colors text-left"
          data-testid="b2b-quick-new-repair">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center"><PlusCircle className="w-5 h-5 text-purple-400" /></div>
          <div><p className="text-white font-semibold text-sm">{t('b2b.newRepair')}</p><p className="text-xs text-gray-400">{t('b2b.registerDevice')}</p></div>
        </button>
        <button onClick={() => onNavigate(TABS.REPAIRS)}
          className="flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl hover:bg-blue-500/20 transition-colors text-left"
          data-testid="b2b-quick-view-repairs">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center"><Wrench className="w-5 h-5 text-blue-400" /></div>
          <div><p className="text-white font-semibold text-sm">{t('b2b.myRepairs')}</p><p className="text-xs text-gray-400">{t('b2b.viewAll')}</p></div>
        </button>
      </div>
      <div>
        <h2 className="text-lg font-bold text-white mb-3">{t('b2b.recentRepairs')}</h2>
        {(!dashboard?.recent_repairs || dashboard.recent_repairs.length === 0) ? (
          <div className="text-center py-8 bg-white/5 border border-white/10 rounded-xl">
            <Wrench className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">{t('b2b.noRepairs')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {dashboard.recent_repairs.map(r => <RepairRow key={r.ref} repair={r} onClick={() => onViewRepair(r)} />)}
          </div>
        )}
      </div>
    </div>
  );
};

// ========== PATTERN LOCK DRAWER ==========
const PatternLockDrawer = ({ value, onChange }) => {
  const { t } = useLanguage();
  const [selectedDots, setSelectedDots] = useState(value ? value.split('-').map(Number) : []);
  const [drawing, setDrawing] = useState(false);
  const gridRef = React.useRef(null);

  const handleDot = (idx) => {
    if (drawing && !selectedDots.includes(idx)) {
      const next = [...selectedDots, idx];
      setSelectedDots(next);
      onChange(next.join('-'));
    }
  };

  const startDraw = (idx) => {
    setDrawing(true);
    setSelectedDots([idx]);
    onChange(String(idx));
  };

  const endDraw = () => setDrawing(false);

  const handleTouchMove = (e) => {
    if (!drawing || !gridRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    const buttons = gridRef.current.querySelectorAll('[data-dot-idx]');
    for (const btn of buttons) {
      const rect = btn.getBoundingClientRect();
      if (touch.clientX >= rect.left && touch.clientX <= rect.right &&
          touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
        const idx = parseInt(btn.getAttribute('data-dot-idx'));
        handleDot(idx);
        break;
      }
    }
  };

  const reset = () => { setSelectedDots([]); onChange(''); };

  return (
    <div data-testid="pattern-lock-drawer">
      <div ref={gridRef} className="grid grid-cols-3 gap-4 w-32 mx-auto my-2"
        onMouseUp={endDraw} onTouchEnd={endDraw} onTouchMove={handleTouchMove}>
        {[1,2,3,4,5,6,7,8,9].map(i => (
          <button key={i} type="button" data-dot-idx={i}
            onMouseDown={() => startDraw(i)} onMouseEnter={() => handleDot(i)}
            onTouchStart={(e) => { e.preventDefault(); startDraw(i); }}
            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${selectedDots.includes(i) ? 'bg-purple-500 border-purple-400 text-white scale-110' : 'bg-white/5 border-white/20 text-gray-500 hover:border-purple-400'}`}>
            {selectedDots.indexOf(i) >= 0 ? selectedDots.indexOf(i) + 1 : i}
          </button>
        ))}
      </div>
      {selectedDots.length > 0 && (
        <div className="flex items-center justify-center gap-2 mt-1">
          <span className="text-xs text-purple-400 font-mono">{selectedDots.join(' → ')}</span>
          <button type="button" onClick={reset} className="text-xs text-red-400 hover:text-red-300">{t('b2bDashboard.clear')}</button>
        </div>
      )}
    </div>
  );
};

// ========== BRAND/MODEL DATA ==========
import { BrandSelector, ModelSelector, useBrandsModels } from '../components/BrandModelSelector';

// ========== NEW REPAIR TAB ==========
const NewRepairTab = ({ api, onSuccess, onViewRepair }) => {
  const { t, tError } = useLanguage();
  const [form, setForm] = useState({
    brand: '', model: '', problem: '', imei_serial: '',
    lock_type: 'code', device_lock: '', notes: '',
    back_glass_broken: false, customer_provides_part: false,
    parts_check: {}, selected_parts: [],
    delivery_method: 'boutique', tracking_number: '', pickup_address: ''
  });
  const [photos, setPhotos] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState(null);
  const [formError, setFormError] = useState('');
  const { brands: dynamicBrands, modelsMap: dynamicModelsMap, seriesMap: dynamicSeriesMap } = useBrandsModels(api);

  const togglePart = (key) => {
    setForm(prev => ({ ...prev, parts_check: { ...prev.parts_check, [key]: !prev.parts_check[key] } }));
  };

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files);
    setPhotos(prev => [...prev, ...files]);
  };

  const removePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.brand || !form.model) { setFormError(t('b2bDashboard.brandModelRequired')); return; }
    if (form.delivery_method === 'postal' && !form.tracking_number) { setFormError(t('b2bDashboard.trackingRequired')); return; }
    if (form.delivery_method === 'recuperation' && !form.pickup_address) { setFormError(t('b2bDashboard.pickupAddressRequired')); return; }
    setFormLoading(true);
    try {
      const res = await api.post('/b2b/repairs', form);
      const createdRepair = res.data;
      if (photos.length > 0) {
        const fd = new FormData();
        photos.forEach(f => fd.append('files', f));
        await api.post(`/b2b/repairs/${createdRepair.ref}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setFormSuccess(createdRepair);
      setForm({ brand: '', model: '', problem: '', imei_serial: '', lock_type: 'code', device_lock: '', notes: '', back_glass_broken: false, customer_provides_part: false, parts_check: {}, selected_parts: [], delivery_method: 'boutique', tracking_number: '', pickup_address: '' });
      setPhotos([]);
      onSuccess();
    } catch (err) {
      setFormError(tError(err.response?.data?.detail) || t('b2bDashboard.submitError'));
    } finally { setFormLoading(false); }
  };

  if (formSuccess) {
    return (
      <div className="max-w-lg mx-auto text-center space-y-6 py-8" data-testid="b2b-repair-success">
        <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-white">{t('b2b.repairRegistered')}</h2>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
          <p className="text-sm text-gray-400">{t('b2b.reference')}</p>
          <p className="text-2xl font-mono font-bold text-purple-400" data-testid="b2b-new-repair-ref">{formSuccess.ref}</p>
          <p className="text-sm text-gray-400 mt-2">{formSuccess.brand} {formSuccess.model}</p>
          {formSuccess.delivery_method === 'postal' && (
            <p className="text-xs text-orange-400 mt-2">{t('b2bDashboard.awaitingReceptionStore')}</p>
          )}
          {formSuccess.delivery_method === 'recuperation' && (
            <p className="text-xs text-amber-400 mt-2">{t('b2bDashboard.pickupRequested')}</p>
          )}
        </div>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
          <p className="text-sm text-purple-300">{t('b2bDashboard.confirmOrderEmailPre')}<strong>{t('b2bDashboard.documentsWord')}</strong>{t('b2bDashboard.confirmOrderEmailPost')}</p>
        </div>
        <div className="flex gap-3 justify-center">
          <button onClick={() => onViewRepair(formSuccess)} className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm font-medium transition-colors" data-testid="b2b-view-created-repair">{t('b2b.viewDetails')}</button>
          <button onClick={() => setFormSuccess(null)} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-medium transition-colors" data-testid="b2b-create-another-repair">{t('b2b.anotherRepair')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="b2b-new-repair-tab">
      <h1 className="text-2xl font-bold text-white mb-1">{t('b2b.newRepair')}</h1>
      <p className="text-gray-400 text-sm mb-6">{t('b2b.registerDevice')}</p>

      {formError && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="b2b-repair-form-error">{formError}</div>}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Device Info */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Smartphone className="w-4 h-4 text-purple-400" />{t('b2b.deviceInfo')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('b2b.brand')} *</label>
              <BrandSelector value={form.brand}
                onChange={v => setForm({...form, brand: v, model: ''})}
                brands={dynamicBrands}
                accentColor="purple"
                testId="b2b-repair-brand" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('b2b.model')} *</label>
              <ModelSelector value={form.model}
                onChange={v => setForm({...form, model: v})}
                models={dynamicModelsMap[form.brand] || []}
                series={dynamicSeriesMap[form.brand] || {}}
                brand={form.brand}
                accentColor="purple"
                testId="b2b-repair-model" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('b2bDashboard.imeiSerial')}</label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input type="text" value={form.imei_serial} onChange={e => setForm({...form, imei_serial: e.target.value})}
                  className="w-full pl-9 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none"
                  placeholder={t('b2bDashboard.optional')} data-testid="b2b-repair-imei" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('b2bDashboard.backGlass')}</label>
              <button type="button" onClick={() => setForm({...form, back_glass_broken: !form.back_glass_broken})}
                className={`w-full px-3 py-2.5 rounded-xl border text-sm text-left transition-all flex items-center gap-2 ${form.back_glass_broken ? 'bg-red-500/20 border-red-500/50 text-red-400' : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'}`}
                data-testid="b2b-repair-back-glass">
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${form.back_glass_broken ? 'border-red-400 bg-red-500' : 'border-gray-500'}`}>
                  {form.back_glass_broken && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                {form.back_glass_broken ? t('b2bDashboard.backGlassBroken') : t('b2bDashboard.backGlassIntact')}
              </button>
              <button type="button" onClick={() => setForm({...form, customer_provides_part: !form.customer_provides_part})}
                className={`w-full px-3 py-2.5 rounded-xl border text-sm text-left transition-all flex items-center gap-2 mt-2 ${form.customer_provides_part ? 'bg-blue-500/20 border-blue-500/50 text-blue-400' : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'}`}
                data-testid="b2b-repair-customer-provides-part">
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${form.customer_provides_part ? 'border-blue-400 bg-blue-500' : 'border-gray-500'}`}>
                  {form.customer_provides_part && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                {form.customer_provides_part ? t('b2bDashboard.customerProvidesPart') : t('b2bDashboard.partProvidedByStore')}
              </button>
            </div>
          </div>

          {/* Lock Type */}
          <div>
            <label className="block text-xs text-gray-400 mb-2">{t('b2bDashboard.lockCode')}</label>
            <div className="flex gap-2 mb-2">
              <button type="button" onClick={() => setForm({...form, lock_type: 'code', device_lock: ''})}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${form.lock_type === 'code' ? 'bg-purple-500/20 border-purple-500/50 text-purple-300' : 'bg-white/5 border-white/10 text-gray-400'}`}
                data-testid="b2b-lock-type-code">
                <Lock className="w-3 h-3 inline mr-1" />{t('b2bDashboard.codeDigitsLetters')}
              </button>
              <button type="button" onClick={() => setForm({...form, lock_type: 'schema', device_lock: ''})}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${form.lock_type === 'schema' ? 'bg-purple-500/20 border-purple-500/50 text-purple-300' : 'bg-white/5 border-white/10 text-gray-400'}`}
                data-testid="b2b-lock-type-schema">
                <Lock className="w-3 h-3 inline mr-1" />{t('b2bDashboard.pattern')}
              </button>
            </div>
            {form.lock_type === 'code' ? (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input type="text" value={form.device_lock} onChange={e => setForm({...form, device_lock: e.target.value})}
                  className="w-full pl-9 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none"
                  placeholder={t('b2bDashboard.enterPinPlaceholder')} data-testid="b2b-repair-lock" />
              </div>
            ) : (
              <PatternLockDrawer value={form.device_lock} onChange={val => setForm({...form, device_lock: val})} />
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">{t('b2bDashboard.problemDescription')}</label>
            <textarea value={form.problem} onChange={e => setForm({...form, problem: e.target.value})} rows={2}
              className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none resize-none"
              placeholder={t('b2bDashboard.describeProblemPlaceholder')} data-testid="b2b-repair-problem" />
          </div>
        </div>

        {/* Parts Check */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" />{t('b2b.brokenParts')}</h3>
          <div className="flex flex-wrap gap-2">
            {partsOptions.map(({ key, labelKey }) => (
              <label key={key} className={`px-3 py-1.5 rounded-xl border cursor-pointer text-xs font-medium transition-all ${form.parts_check[key] ? 'bg-red-500/20 border-red-500/50 text-red-400' : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'}`}>
                <input type="checkbox" checked={form.parts_check[key] || false} onChange={() => togglePart(key)} className="hidden" />
                {t(labelKey)}
              </label>
            ))}
          </div>
        </div>

        {/* Delivery Method */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Truck className="w-4 h-4 text-cyan-400" />{t('b2b.deliveryMethod')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button type="button" onClick={() => setForm({...form, delivery_method: 'boutique', tracking_number: '', pickup_address: ''})}
              className={`p-3 rounded-xl border text-left transition-all ${form.delivery_method === 'boutique' ? 'bg-purple-500/20 border-purple-500/50 text-purple-300' : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'}`}
              data-testid="b2b-delivery-boutique">
              <MapPin className="w-5 h-5 mb-1" />
              <p className="text-sm font-semibold">{t('b2b.inStore')}</p>
              <p className="text-xs opacity-60">{t('b2b.inStoreDesc')}</p>
            </button>
            <button type="button" onClick={() => setForm({...form, delivery_method: 'postal', pickup_address: ''})}
              className={`p-3 rounded-xl border text-left transition-all ${form.delivery_method === 'postal' ? 'bg-purple-500/20 border-purple-500/50 text-purple-300' : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'}`}
              data-testid="b2b-delivery-postal">
              <MailIcon className="w-5 h-5 mb-1" />
              <p className="text-sm font-semibold">{t('b2b.postal')}</p>
              <p className="text-xs opacity-60">{t('b2b.postalDesc')}</p>
            </button>
            <button type="button" onClick={() => setForm({...form, delivery_method: 'recuperation', tracking_number: ''})}
              className={`p-3 rounded-xl border text-left transition-all ${form.delivery_method === 'recuperation' ? 'bg-purple-500/20 border-purple-500/50 text-purple-300' : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'}`}
              data-testid="b2b-delivery-recuperation">
              <Truck className="w-5 h-5 mb-1" />
              <p className="text-sm font-semibold">{t('b2b.pickup')}</p>
              <p className="text-xs opacity-60">{t('b2b.pickupDesc')}</p>
            </button>
          </div>
          {form.delivery_method === 'postal' && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('b2bDashboard.trackingNumber')} *</label>
              <input type="text" value={form.tracking_number} onChange={e => setForm({...form, tracking_number: e.target.value})}
                className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none"
                placeholder={t('b2bDashboard.trackingPlaceholder')} data-testid="b2b-repair-tracking" />
            </div>
          )}
          {form.delivery_method === 'recuperation' && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('b2bDashboard.pickupAddress')} *</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-500" />
                <textarea value={form.pickup_address} onChange={e => setForm({...form, pickup_address: e.target.value})} rows={2}
                  className="w-full pl-9 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none resize-none"
                  placeholder={t('b2bDashboard.pickupAddressPlaceholder')} data-testid="b2b-repair-pickup-address" />
              </div>
            </div>
          )}
        </div>

        {/* Photos */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Camera className="w-4 h-4 text-blue-400" />{t('b2b.devicePhotos')}</h3>
          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-purple-500/50 transition-colors">
            <Upload className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-400">{t('b2bDashboard.clickToAddPhotos')}</span>
            <input type="file" multiple accept="image/*" onChange={handlePhotos} className="hidden" data-testid="b2b-repair-photos" />
          </label>
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {photos.map((f, i) => (
                <div key={i} className="relative group">
                  <img src={URL.createObjectURL(f)} alt="" className="w-16 h-16 object-cover rounded-lg border border-white/10" />
                  <button type="button" onClick={() => removePhoto(i)} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-600 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs text-gray-400 mb-1">{t('b2bDashboard.additionalNotes')}</label>
          <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2}
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none resize-none"
            placeholder={t('b2bDashboard.additionalInfoPlaceholder')} data-testid="b2b-repair-notes" />
        </div>

        <button type="submit" disabled={formLoading}
          className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="b2b-repair-submit">
          {formLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {formLoading ? t('b2b.submitting') : t('b2b.submitRepair')}
        </button>
      </form>
    </div>
  );
};

// ========== REPAIRS LIST TAB ==========
const RepairsListTab = ({ repairs, allRepairs, statusFilter, setStatusFilter, search, setSearch, onViewRepair }) => {
  const { t, tError } = useLanguage();
  const statusCounts = {};
  allRepairs.forEach(r => { statusCounts[r.status] = (statusCounts[r.status] || 0) + 1; });

  const filterTabs = [
    { key: '', label: `${t('b2bDashboard.filter.all')} (${allRepairs.length})` },
    { key: 'En attente de réception', label: `${statusLabel(t, 'En attente de réception')} (${statusCounts['En attente de réception'] || 0})` },
    { key: 'Reçu', label: `${statusLabel(t, 'Reçu')} (${statusCounts['Reçu'] || 0})` },
    { key: 'En Cours', label: `${statusLabel(t, 'En Cours')} (${statusCounts['En Cours'] || 0})` },
    { key: 'Attente Pièces', label: `${statusLabel(t, 'Attente Pièces')} (${statusCounts['Attente Pièces'] || 0})` },
    { key: 'Terminé', label: `${statusLabel(t, 'Terminé')} (${statusCounts['Terminé'] || 0})` },
    { key: 'Livré', label: `${statusLabel(t, 'Livré')} (${statusCounts['Livré'] || 0})` },
  ];

  return (
    <div className="space-y-4" data-testid="b2b-repairs-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-white">{t('b2b.myRepairs')}</h1>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t('b2bDashboard.searchPlaceholder')} className="pl-10 pr-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm w-60" data-testid="b2b-repairs-search" />
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {filterTabs.map(ft => (
          <button key={ft.key} onClick={() => setStatusFilter(ft.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${statusFilter === ft.key ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}
            data-testid={`b2b-filter-${ft.key || 'all'}`}>
            {ft.label}
          </button>
        ))}
      </div>
      {repairs.length === 0 ? (
        <div className="text-center py-10 bg-white/5 border border-white/10 rounded-xl">
          <Wrench className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">{t('b2b.noRepairFound')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {repairs.map(r => <RepairRow key={r.ref} repair={r} onClick={() => onViewRepair(r)} />)}
        </div>
      )}
    </div>
  );
};

// ========== REPAIR DETAIL TAB ==========
const RepairDetailTab = ({ repair, api, onBack, onRefresh }) => {
  const { t, tError, formatCurrency, formatDate } = useLanguage();
  const config = STATUS_CONFIG[repair.status] || STATUS_CONFIG['Reçu'];
  const StatusIcon = config.icon;
  const [uploading, setUploading] = useState(false);
  const [showPhotos, setShowPhotos] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const handleSaveNote = async () => {
    if (!note.trim() && !repair.notes) return;
    setSavingNote(true);
    try {
      await api.put(`/b2b/repairs/${repair.ref}/note`, { notes: note.trim() });
      onRefresh();
    } catch { alert(t('b2bDashboard.noteSaveError')); }
    finally { setSavingNote(false); }
  };

  React.useEffect(() => { setNote(repair.notes || ''); }, [repair.notes]);

  const handlePdf = async () => {
    try {
      const res = await api.get(`/b2b/repairs/${repair.ref}/fiche-pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) { alert(t('b2bDashboard.pdfDownloadError')); }
  };

  const handleUploadMore = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      await api.post(`/b2b/repairs/${repair.ref}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onRefresh();
    } catch (err) { alert(t('b2bDashboard.uploadError')); }
    finally { setUploading(false); }
  };

  const brokenParts = Object.entries(repair.parts_check || {}).filter(([, v]) => v).map(([k]) => partLabel(t, k));
  const lockLabel = repair.lock_type === 'schema' ? t('b2bDashboard.pattern') : t('b2bDashboard.code');
  const deliveryLabels = { boutique: t('b2bDashboard.delivery.store'), postal: t('b2bDashboard.delivery.postal'), recuperation: t('b2bDashboard.delivery.pickup') };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="b2b-repair-detail">
      <button onClick={onBack} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm" data-testid="b2b-repair-back">
        <ArrowLeft className="w-4 h-4" /> {t('b2bDashboard.back')}
      </button>

      {/* Header */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs text-gray-500">{t('b2b.reference')}</p>
            <p className="text-xl font-mono font-bold text-purple-400" data-testid="b2b-detail-ref">{repair.ref}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setChatOpen(true)} className="p-2 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors" title={t('b2bDashboard.chat')} data-testid="b2b-repair-chat-btn">
              <MessageSquare className="w-4 h-4" />
            </button>
            <button onClick={handlePdf} className="p-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors" title={t('b2bDashboard.downloadPdf')} data-testid="b2b-repair-pdf">
              <Printer className="w-4 h-4" />
            </button>
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${config.bg} ${config.color}`} data-testid="b2b-detail-status">
              <StatusIcon className="w-3 h-3 inline mr-1" />{statusLabel(t, repair.status, config.label)}
            </span>
          </div>
        </div>

        {/* Staff decision badge */}
        {repair.staff_accepted === true && (
          <div className="mb-3 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-sm text-green-400 font-medium">{t('b2bDashboard.repairAcceptedByStaff')}</span>
          </div>
        )}
        {repair.staff_accepted === false && (
          <div className="mb-3 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm text-red-400 font-medium">{t('b2bDashboard.repairRefusedByStaff')}</span>
          </div>
        )}
        {repair.confirmation_validated && (
          <div className="mb-3 px-3 py-2 bg-purple-500/10 border border-purple-500/30 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-purple-400 font-medium">{t('b2bDashboard.orderValidated')}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><p className="text-gray-500">{t('b2b.brand')}</p><p className="text-white font-medium">{repair.brand}</p></div>
          <div><p className="text-gray-500">{t('b2b.model')}</p><p className="text-white font-medium">{repair.model}</p></div>
          {repair.imei_serial && <div><p className="text-gray-500">{t('b2bDashboard.imeiSerialShort')}</p><p className="text-white font-mono">{repair.imei_serial}</p></div>}
          {repair.device_lock && <div><p className="text-gray-500">{t('b2bDashboard.lock')} ({lockLabel})</p><p className="text-white">{repair.lock_type === 'schema' ? `${t('b2bDashboard.pattern')}: ${repair.device_lock}` : repair.device_lock}</p></div>}
          {repair.back_glass_broken && <div><p className="text-gray-500">{t('b2bDashboard.backGlass')}</p><p className="text-red-400 font-medium">{t('b2bDashboard.broken')}</p></div>}
          {repair.problem && <div className="col-span-2"><p className="text-gray-500">{t('b2bDashboard.problem')}</p><p className="text-white">{repair.problem}</p></div>}
          <div><p className="text-gray-500">{t('b2bDashboard.delivery')}</p><p className="text-white">{deliveryLabels[repair.delivery_method] || repair.delivery_method}</p></div>
          {repair.tracking_number && <div><p className="text-gray-500">{t('b2bDashboard.trackingShort')}</p><p className="text-white font-mono">{repair.tracking_number}</p></div>}
          {repair.pickup_address && <div className="col-span-2"><p className="text-gray-500">{t('b2bDashboard.pickupAddress')}</p><p className="text-white">{repair.pickup_address}</p></div>}
          {(repair.price_repair > 0 || repair.price_diagnostic > 0) && (
            <>
              <div><p className="text-gray-500">{t('b2bDashboard.priceRepair')}</p><p className="text-white font-bold">{formatCurrency(repair.price_repair || 0)}</p></div>
              <div><p className="text-gray-500">{t('b2bDashboard.priceDiagnostic')}</p><p className="text-white">{formatCurrency(repair.price_diagnostic || 0)}</p></div>
            </>
          )}
          {repair.price_repair === 0 && repair.price_diagnostic === 0 && (
            <div className="col-span-2"><p className="text-gray-500">{t('b2bDashboard.price')}</p><p className="text-yellow-400 text-xs">{t('b2bDashboard.pricePending')}</p></div>
          )}
          {repair.notes && <div className="col-span-2"><p className="text-gray-500">{t('b2bDashboard.notes')}</p><p className="text-white">{repair.notes}</p></div>}
          <div><p className="text-gray-500">{t('b2bDashboard.createdOn')}</p><p className="text-white">{formatDate(repair.created_at, { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p></div>
        </div>

        {brokenParts.length > 0 && (
          <div className="mt-3">
            <p className="text-gray-500 text-sm mb-1">{t('b2bDashboard.nonFunctionalParts')}</p>
            <div className="flex flex-wrap gap-1">
              {brokenParts.map(p => <span key={p} className="px-2 py-0.5 rounded-full bg-red-500/20 border border-red-500/30 text-red-400 text-xs">{p}</span>)}
            </div>
          </div>
        )}
      </div>

      {/* Photos */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold text-sm flex items-center gap-2"><ImageIcon className="w-4 h-4 text-blue-400" />{t('b2bDashboard.photos')} ({repair.photos?.length || 0})</h3>
          <label className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors ${uploading ? 'bg-gray-500/20 text-gray-400' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/30'}`}>
            {uploading ? t('b2bDashboard.uploading') : t('b2bDashboard.addPhoto')}
            <input type="file" multiple accept="image/*" onChange={handleUploadMore} className="hidden" disabled={uploading} data-testid="b2b-repair-add-photos" />
          </label>
        </div>
        {repair.photos?.length > 0 ? (
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {repair.photos.map((url, i) => (
              <a key={i} href={`${API_URL}${url}`} target="_blank" rel="noreferrer" className="block">
                <img src={`${API_URL}${url}`} alt="" className="w-full aspect-square object-cover rounded-lg border border-white/10 hover:border-purple-500/50 transition-colors" />
              </a>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm text-center py-3">{t('b2bDashboard.noPhoto')}</p>
        )}
      </div>

      {/* Timeline */}
      {repair.timeline?.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-bold text-sm mb-4">{t('b2bDashboard.history')}</h3>
          <div className="space-y-3">
            {repair.timeline.map((ev, i) => {
              const evConfig = STATUS_CONFIG[ev.status] || STATUS_CONFIG['Reçu'];
              return (
                <div key={i} className="flex gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${evConfig.color.replace('text-', 'bg-')}`} />
                  <div>
                    <p className="text-white text-sm font-medium">{statusLabel(t, ev.status, ev.label)}</p>
                    <p className="text-xs text-gray-500">
                      {formatDate(ev.timestamp, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      {ev.actor && ` - ${ev.actor}`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Notes B2B */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
        <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4 text-yellow-400" /> {t('b2bDashboard.notes')}
        </h3>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:border-purple-500/40 outline-none resize-none"
          rows={3}
          placeholder={t('b2bDashboard.addNotePlaceholder')}
          data-testid="b2b-repair-note-input"
        />
        {note !== (repair.notes || '') && (
          <button onClick={handleSaveNote} disabled={savingNote}
            className="mt-2 px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
            data-testid="b2b-repair-note-save">
            {savingNote ? t('b2bDashboard.saving') : t('b2bDashboard.saveNote')}
          </button>
        )}
      </div>

      {/* Chat Modal for this repair */}
      {chatOpen && (
        <ChatModal
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          relatedType="b2b_repair"
          relatedId={repair.ref}
          title={t('b2bDashboard.repairTitle', { ref: repair.ref })}
          apiPrefix="/b2b/chat"
        />
      )}
    </div>
  );
};

// ========== DOCUMENTS TAB ==========
const DocumentsTab = ({ api }) => {
  const { t, tError, formatCurrency, formatDate } = useLanguage();
  const [invoices, setInvoices] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [posInvoices, setPosInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [confirmCodes, setConfirmCodes] = useState({});
  const [confirmLoading, setConfirmLoading] = useState(null);
  const [confirmMsg, setConfirmMsg] = useState({});
  const [payingRef, setPayingRef] = useState(null);
  const [invoiceSortOrder, setInvoiceSortOrder] = useState('desc');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [invRes, repRes, posRes] = await Promise.all([
          api.get('/b2b/invoices'),
          api.get('/b2b/repairs'),
          api.get('/b2b/pos-invoices').catch(() => ({ data: [] }))
        ]);
        setInvoices(invRes.data);
        setRepairs(repRes.data);
        setPosInvoices(posRes.data || []);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchData();
  }, [api]);

  const downloadInvoice = async (ref) => {
    setDownloading(ref);
    try {
      const res = await api.get(`/b2b/invoices/${ref}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `FACTURE_${ref}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { alert(t('b2bDashboard.downloadError')); }
    setDownloading(null);
  };

  const handleConfirmCode = async (ref) => {
    const code = (confirmCodes[ref] || '').trim();
    if (!code || code.length !== 6) {
      setConfirmMsg(prev => ({ ...prev, [ref]: { type: 'error', text: t('b2bDashboard.enter6DigitCode') } }));
      return;
    }
    setConfirmLoading(ref);
    try {
      await api.post(`/b2b/repairs/${ref}/confirm-code`, { code });
      setConfirmMsg(prev => ({ ...prev, [ref]: { type: 'success', text: t('b2bDashboard.orderValidatedSuccess') } }));
      // Refresh repairs to update confirmation status
      const repRes = await api.get('/b2b/repairs');
      setRepairs(repRes.data);
    } catch (err) {
      setConfirmMsg(prev => ({ ...prev, [ref]: { type: 'error', text: tError(err.response?.data?.detail) || t('b2bDashboard.incorrectCode') } }));
    }
    setConfirmLoading(null);
  };

  const handlePayOnline = async (ref) => {
    setPayingRef(ref);
    try {
      const res = await api.post('/payments/stripe/create-checkout', {
        order_type: 'b2b_repair',
        device_ref: ref
      });
      const { checkout_url } = res.data;
      if (checkout_url) {
        window.open(checkout_url, '_blank');
      } else {
        alert(t('b2bDashboard.paymentLinkUnavailable'));
      }
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('b2bDashboard.paymentCreateError'));
    }
    setPayingRef(null);
  };

  const downloadPosInvoice = async (refVente) => {
    setDownloading(refVente);
    try {
      const res = await api.get(`/client/invoices/${refVente}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Facture_${refVente}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { alert(t('b2bDashboard.downloadError')); }
    setDownloading(null);
  };

  // Repairs pending confirmation (not yet validated)
  const pendingConfirmation = repairs.filter(r => !r.confirmation_validated);

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 text-purple-400 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="b2b-documents-tab">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-white">{t('b2b.documents')}</h1>
        <p className="text-gray-400 text-sm mt-1">{t('b2b.documentsDesc')}</p>
      </div>

      {/* Pending Confirmation Section */}
      {pendingConfirmation.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Lock className="w-5 h-5 text-purple-400" />
            {t('b2b.pendingValidation')}
          </h2>
          <p className="text-xs text-gray-400">{t('b2bDashboard.enterCodePerOrder')}</p>
          {pendingConfirmation.map(r => (
            <div key={r.ref} className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-xl" data-testid={`b2b-confirm-row-${r.ref}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <span className="text-white font-mono font-bold text-sm">{r.ref}</span>
                  <span className="text-gray-400 text-sm ml-2">{r.brand} {r.model}</span>
                  <p className="text-xs text-gray-500 mt-0.5">{formatDate(r.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    maxLength={6}
                    value={confirmCodes[r.ref] || ''}
                    onChange={e => setConfirmCodes(prev => ({ ...prev, [r.ref]: e.target.value.replace(/\D/g, '') }))}
                    placeholder={t('b2bDashboard.code6Digits')}
                    className="w-36 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm text-center font-mono tracking-widest focus:border-purple-500/50 focus:outline-none"
                    data-testid={`b2b-confirm-input-${r.ref}`}
                  />
                  <button
                    onClick={() => handleConfirmCode(r.ref)}
                    disabled={confirmLoading === r.ref}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-bold transition-colors disabled:opacity-50 flex items-center gap-1"
                    data-testid={`b2b-confirm-btn-${r.ref}`}>
                    {confirmLoading === r.ref ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                    {t('b2bDashboard.validate')}
                  </button>
                </div>
              </div>
              {confirmMsg[r.ref] && (
                <p className={`text-xs mt-2 ${confirmMsg[r.ref].type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                  {confirmMsg[r.ref].text}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Invoices Section */}
      <div className="space-y-3">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-green-400" />
          {t('b2bDashboard.invoices')}
        </h2>
        {invoices.length === 0 ? (
          <div className="text-center py-12 bg-white/5 border border-white/10 rounded-xl">
            <FolderOpen className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">{t('b2bDashboard.noInvoiceAvailable')}</p>
            <p className="text-gray-500 text-sm mt-1">{t('b2bDashboard.invoicesAppearWhenPriced')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 text-xs text-gray-500 font-semibold uppercase tracking-wide">
              <div className="col-span-2">{t('b2b.reference')}</div>
              <div className="col-span-3">{t('b2bDashboard.device')}</div>
              <div className="col-span-1">{t('b2bDashboard.statusCol')}</div>
              <div className="col-span-2">{t('b2bDashboard.amount')}</div>
              <div className="col-span-2">{t('b2bDashboard.date')}</div>
              <div className="col-span-2">{t('b2bDashboard.actions')}</div>
            </div>
            {invoices.filter(inv => !inv.cancelled).map(inv => {
              const invConfig = STATUS_CONFIG[inv.status] || STATUS_CONFIG['Terminé'];
              return (
                <div key={`${inv.invoice_id}`} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/[0.07] transition-colors" data-testid={`b2b-invoice-row-${inv.invoice_id}`}>
                  <div className="md:col-span-2">
                    <span className="text-white font-mono font-bold text-sm">{inv.invoice_number || inv.ref}</span>
                    <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded font-bold ${
                      inv.invoice_type === 'diagnostic' ? 'bg-cyan-500/20 text-cyan-400' :
                      inv.invoice_type === 'repair' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-orange-500/20 text-orange-400'
                    }`}>
                      {inv.invoice_type === 'diagnostic' ? t('b2bDashboard.invoiceType.diag') : inv.invoice_type === 'repair' ? t('b2bDashboard.invoiceType.repair') : t('b2bDashboard.invoiceType.combined')}
                    </span>
                    <p className="text-xs text-gray-500 md:hidden">{inv.brand} {inv.model}</p>
                  </div>
                  <div className="md:col-span-3 hidden md:block">
                    <p className="text-white text-sm">{inv.brand} {inv.model}</p>
                    <p className="text-xs text-gray-500 truncate">{inv.problem}</p>
                  </div>
                  <div className="md:col-span-1 hidden md:block">
                    {inv.payment_status === 'paid' ? (
                      <span className="text-xs px-2 py-0.5 rounded-full border bg-green-500/20 text-green-400 border-green-500/30">{t('b2bDashboard.paid')}</span>
                    ) : (
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${invConfig.bg} ${invConfig.color}`}>
                        {statusLabel(t, inv.status, invConfig.label)}
                      </span>
                    )}
                  </div>
                  <div className="md:col-span-2 hidden md:block">
                    <span className="text-white font-semibold">{formatCurrency(inv.total)}</span>
                    {inv.related_diagnostic_invoice && (
                      <p className="text-xs text-cyan-400">{t('b2bDashboard.diagRef')} {inv.related_diagnostic_invoice}</p>
                    )}
                  </div>
                  <div className="md:col-span-2 hidden md:block">
                    <span className="text-gray-400 text-sm">{inv.invoice_date ? formatDate(inv.invoice_date) : formatDate(inv.created_at)}</span>
                  </div>
                  <div className="md:col-span-2 flex gap-2 flex-wrap">
                    <button onClick={() => downloadInvoice(inv.ref)} disabled={downloading === inv.ref}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/20 border border-purple-500/30 text-purple-300 rounded-lg text-xs font-medium hover:bg-purple-500/30 transition-colors disabled:opacity-50"
                      data-testid={`b2b-invoice-download-${inv.invoice_id}`}>
                      {downloading === inv.ref ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                      PDF
                    </button>
                    {inv.payment_status !== 'paid' && inv.total > 0 && !inv.cancelled && (
                      <button onClick={() => handlePayOnline(inv.ref)} disabled={payingRef === inv.ref}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 border border-green-500/30 text-green-300 rounded-lg text-xs font-medium hover:bg-green-500/30 transition-colors disabled:opacity-50"
                        data-testid={`b2b-invoice-pay-${inv.invoice_id}`}>
                        {payingRef === inv.ref ? <RefreshCw className="w-3 h-3 animate-spin" /> : <DollarSign className="w-3 h-3" />}
                        {t('b2bDashboard.pay')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* POS Invoices Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-red-400" />
            {t('b2bDashboard.posInvoices')}
          </h2>
          {posInvoices.length > 0 && (
            <button
              onClick={() => setInvoiceSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
              className="px-3 py-1.5 rounded-lg bg-white/5 text-gray-300 border border-white/10 text-xs font-semibold flex items-center gap-1 hover:bg-white/10"
              data-testid="b2b-pos-sort-btn"
            >
              {invoiceSortOrder === 'desc' ? t('b2bDashboard.mostRecent') : t('b2bDashboard.oldest')}
            </button>
          )}
        </div>
        {posInvoices.length === 0 ? (
          <div className="text-center py-8 bg-white/5 border border-white/10 rounded-xl">
            <FolderOpen className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">{t('b2bDashboard.noPosInvoice')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {[...posInvoices]
              .sort((a, b) => invoiceSortOrder === 'desc'
                ? new Date(b.created_at) - new Date(a.created_at)
                : new Date(a.created_at) - new Date(b.created_at)
              )
              .map(inv => (
                <div key={inv.id} className="p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/[0.07] transition-colors" data-testid={`b2b-pos-invoice-${inv.ref_vente}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-mono font-bold text-sm">{inv.ref_vente}</span>
                    <span className="text-gray-400 text-sm">{formatDate(inv.created_at)}</span>
                  </div>
                  <div className="space-y-1 mb-3">
                    {(inv.items || []).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-300">{item.designation} x{item.quantite}</span>
                        <span className="text-gray-400">{formatCurrency(item.prix_unitaire || item.prix || 0)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white font-semibold">{formatCurrency(inv.total)}</span>
                    <button onClick={() => downloadPosInvoice(inv.ref_vente)} disabled={downloading === inv.ref_vente}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 border border-red-500/30 text-red-300 rounded-lg text-xs font-medium hover:bg-red-500/30 transition-colors disabled:opacity-50"
                      data-testid={`b2b-pos-download-${inv.ref_vente}`}>
                      {downloading === inv.ref_vente ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                      PDF
                    </button>
                  </div>
                </div>
              ))
            }
          </div>
        )}
      </div>

      {/* Retention notice */}
      <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-1" data-testid="b2b-retention-notice">
        <p className="text-xs text-gray-500">{t('b2bDashboard.retentionInvoices')}</p>
        <p className="text-xs text-gray-500">{t('b2bDashboard.retentionQuotes')}</p>
      </div>
    </div>
  );
};

// ========== SHARED REPAIR ROW ==========
const RepairRow = ({ repair, onClick }) => {
  const { t, formatDate } = useLanguage();
  const config = STATUS_CONFIG[repair.status] || STATUS_CONFIG['Reçu'];
  const StatusIcon = config.icon;
  return (
    <button onClick={onClick}
      className="w-full flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors text-left"
      data-testid={`b2b-repair-row-${repair.ref}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-4 h-4 text-purple-400" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-mono font-bold text-sm">{repair.ref}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${config.bg} ${config.color}`}>
              <StatusIcon className="w-3 h-3 inline mr-0.5" />{statusLabel(t, repair.status, config.label)}
            </span>
            {repair.delivery_method === 'postal' && repair.tracking_number && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">{t('b2bDashboard.trackingBadge')} {repair.tracking_number}</span>
            )}
          </div>
          <p className="text-xs text-gray-400 truncate">{repair.brand} {repair.model}{repair.problem ? ` - ${repair.problem}` : ''}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-xs text-gray-500 hidden sm:block">{formatDate(repair.created_at)}</span>
        <ChevronRight className="w-4 h-4 text-gray-500" />
      </div>
    </button>
  );
};

// ═══════════════════════════════════
//  B2B DEVIS TAB
// ═══════════════════════════════════
const B2BDevisTab = ({ devis, factures, api, onRefresh }) => {
  const { user } = useAuth();
  const { t, tError, formatCurrency, formatDate } = useLanguage();
  const [validationCodes, setValidationCodes] = React.useState({});
  const [validatingCode, setValidatingCode] = React.useState(null);
  const getFacture = (devisId) => factures.find(f => f.devis_id === devisId);

  const handleValidateCode = async (devisId) => {
    const code = validationCodes[devisId];
    if (!code || code.length < 4) { alert(t('b2bDashboard.enterValidationCode')); return; }
    setValidatingCode(devisId);
    try {
      await api.post(`/devis/validate-code/${devisId}`, { code });
      alert(t('b2bDashboard.codeValidatedSuccess'));
      onRefresh();
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('b2bDashboard.incorrectCode'));
    } finally { setValidatingCode(null); }
  };
  const stConfig = {
    en_attente: { label: t('b2bDashboard.devisStatus.pending'), color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    accepte: { label: t('b2bDashboard.devisStatus.accepted'), color: 'bg-green-500/20 text-green-400 border-green-500/30' },
    refuse: { label: t('b2bDashboard.devisStatus.refused'), color: 'bg-red-500/20 text-red-400 border-red-500/30' },
    acompte_verse: { label: t('b2bDashboard.devisStatus.depositPaid'), color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    paye: { label: t('b2bDashboard.devisStatus.paid'), color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  };

  return (
    <div className="space-y-4" data-testid="b2b-devis-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-purple-400" /> {t('b2bDashboard.travelQuotes')}
        </h2>
        <button onClick={onRefresh} className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-gray-400">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {devis.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p>{t('b2bDashboard.noTravelQuote')}</p>
        </div>
      ) : (
        devis.map(d => {
          const facture = getFacture(d.id);
          const total = parseFloat(d.total || 0);
          const paid = facture ? parseFloat(facture.amount_paid || 0) : 0;
          const remaining = Math.max(0, total - paid);
          const effectiveStatus = d.payment_status || d.status;
          const cfg = stConfig[effectiveStatus] || stConfig.en_attente;

          return (
            <div key={d.id} className="bg-white/[.03] border border-white/10 rounded-xl p-4 space-y-3" data-testid={`b2b-devis-${d.ref}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-bold text-purple-400 font-mono">{d.ref}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    {d.created_at ? formatDate(d.created_at) : ''}
                  </span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-bold border ${cfg.color}`}>{cfg.label}</span>
              </div>

              {d.description && <p className="text-sm text-gray-400">{d.description}</p>}

              <div className="rounded-lg border border-white/10 overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-white/5">
                    <th className="text-left p-2 text-gray-400 text-xs">{t('b2bDashboard.designation')}</th>
                    <th className="text-right p-2 text-gray-400 text-xs w-20">{t('b2bDashboard.priceCol')}</th>
                  </tr></thead>
                  <tbody>
                    {d.lignes?.map((l, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="p-2 text-gray-300 text-xs">{l.designation} x{l.quantite}</td>
                        <td className="p-2 text-right text-white text-xs">{formatCurrency(parseFloat(l.prix) * parseInt(l.quantite))}</td>
                      </tr>
                    ))}
                    {d.inclure_deplacement && (
                      <tr className="border-t border-white/5">
                        <td className="p-2 text-gray-300 text-xs">{t('b2bDashboard.travelFee')}</td>
                        <td className="p-2 text-right text-white text-xs">{formatCurrency(parseFloat(d.frais_deplacement || 45))}</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot><tr className="border-t border-white/10 bg-white/5">
                    <td className="p-2 text-right font-bold text-white text-xs">{t('b2bDashboard.total')}</td>
                    <td className="p-2 text-right font-bold text-purple-400">{formatCurrency(total)}</td>
                  </tr></tfoot>
                </table>
              </div>

              {/* Payment info */}
              {facture && d.code_validated && paid > 0 && (
                <div className="flex justify-between items-center p-3 rounded-xl bg-black/30 border border-white/5">
                  <div><div className="text-xs text-gray-400">{t('b2bDashboard.total')}</div><div className="font-bold text-white">{formatCurrency(total)}</div></div>
                  <div><div className="text-xs text-gray-400">{t('b2bDashboard.paidLabel')}</div><div className="font-bold text-green-400">{formatCurrency(paid)}</div></div>
                  <div><div className="text-xs text-gray-400">{t('b2bDashboard.remaining')}</div><div className={`font-bold ${remaining > 0 ? 'text-amber-400' : 'text-green-400'}`}>{formatCurrency(remaining)}</div></div>
                </div>
              )}

              {/* Late penalties */}
              {facture?.late_penalties && remaining > 0 && (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 space-y-1">
                  <p className="text-xs text-red-400 font-bold">{t('b2bDashboard.latePayment', { days: facture.late_penalties.days_late })}</p>
                  <p className="text-xs text-red-300">{t('b2bDashboard.penaltiesLine', { penalty: formatCurrency(facture.late_penalties.penalite_retard), indemnity: formatCurrency(facture.late_penalties.indemnite_forfaitaire) })}</p>
                  <p className="text-xs text-red-400 font-bold">{t('b2bDashboard.totalDue', { amount: formatCurrency(facture.late_penalties.total_with_penalties) })}</p>
                </div>
              )}

              {d.bon_commande_ref && (
                <div className="text-xs text-gray-400">{t('b2bDashboard.purchaseOrderLabel')} <span className="text-green-400 font-semibold">{d.bon_commande_ref}</span></div>
              )}
              {d.facture_ref && (
                <div className="text-xs text-gray-400">{t('b2bDashboard.invoiceLabel')} <span className="text-red-400 font-semibold">{d.facture_ref}</span></div>
              )}

              {/* Validation code input */}
              {d.status === 'accepte' && d.validation_code && !d.code_validated && (
                <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 space-y-2">
                  <p className="text-xs text-amber-400 font-semibold">{t('b2bDashboard.orderValidationCode')}</p>
                  <p className="text-xs text-gray-400">{t('b2bDashboard.enterCodeToValidate')}</p>
                  <div className="flex gap-2">
                    <input type="text" value={validationCodes[d.id] || ''}
                      onChange={e => setValidationCodes(prev => ({ ...prev, [d.id]: e.target.value }))}
                      placeholder={t('b2bDashboard.code6Digits')} maxLength={6}
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-center tracking-widest font-mono text-white placeholder-gray-500 focus:border-amber-500/50 focus:outline-none" />
                    <button onClick={() => handleValidateCode(d.id)} disabled={validatingCode === d.id}
                      className="px-4 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50">
                      {validatingCode === d.id ? '...' : t('b2bDashboard.validate')}
                    </button>
                  </div>
                </div>
              )}

              {d.code_validated && (
                <div className="flex items-center gap-2 text-xs text-green-400">
                  <CheckCircle className="w-4 h-4" /> {t('b2bDashboard.validationCodeConfirmed')}
                </div>
              )}

              {/* PDF downloads + action buttons */}
              <div className="flex gap-2 flex-wrap">
                <a href={`${API_URL}/api/devis/${d.id}/pdf`} target="_blank" rel="noreferrer"
                  className="px-3 py-1.5 rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 text-xs font-semibold flex items-center gap-1">
                  <Download className="w-3 h-3" /> {t('b2bDashboard.pdfQuote')}
                </a>
                {d.bon_commande_ref && (
                  <a href={`${API_URL}/api/devis/${d.id}/bon-pdf`} target="_blank" rel="noreferrer"
                    className="px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 text-xs font-semibold flex items-center gap-1">
                    <Download className="w-3 h-3" /> {t('b2bDashboard.pdfPurchaseOrder')}
                  </a>
                )}
                {facture && (
                  <a href={`${API_URL}/api/facture-dep/${facture.id}/pdf`} target="_blank" rel="noreferrer"
                    className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 text-xs font-semibold flex items-center gap-1">
                    <Download className="w-3 h-3" /> {t('b2bDashboard.pdfInvoice')}
                  </a>
                )}
                {d.status === 'en_attente' && (
                  <a href={`/devis/accepter/${d.id}`}
                    className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> {t('b2bDashboard.acceptRefuse')}
                  </a>
                )}
                {d.status === 'accepte' && d.code_validated && remaining > 0 && (
                  <a href={`/devis/paiement/${d.id}?mode=acompte`}
                    className="px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 text-xs font-semibold flex items-center gap-1">
                    <CreditCard className="w-3 h-3" /> {paid > 0 ? t('b2bDashboard.payRemaining', { amount: formatCurrency(remaining) }) : t('b2bDashboard.pay')}
                  </a>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* Chat Widget */}
      <ClientChatWidget api={api} user={user} b2b={true} />
    </div>
  );
};

export default B2BDashboardPage;

// ========== B2B QUOTE REQUEST TAB ==========
const B2BQuoteRequestTab = ({ requests, showForm, setShowForm, form, setForm, onSubmit, submitting }) => {
  const { t, formatDate } = useLanguage();
  return (
  <div className="space-y-4 fade-in" data-testid="b2b-quote-request-tab">
    <div className="flex items-center justify-between">
      <h2 className="text-xl font-bold text-cyan-400">{t('b2bDashboard.nav.quoteRequest')}</h2>
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/30 font-semibold text-sm transition-all"
          data-testid="b2b-new-devis-request-btn"
        >
          <PlusCircle className="w-4 h-4" /> {t('b2bDashboard.newRequest')}
        </button>
      )}
    </div>

    {showForm && (
      <div className="card-dark p-5 space-y-4" data-testid="b2b-devis-request-form">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-white">{t('b2bDashboard.newQuoteRequest')}</h3>
          <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">{t('b2bDashboard.titleOptional')}</label>
          <input
            value={form.titre}
            onChange={e => setForm(f => ({ ...f, titre: e.target.value }))}
            className="input-dark w-full py-2"
            placeholder={t('b2bDashboard.titlePlaceholder')}
            data-testid="b2b-request-titre"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">{t('b2bDashboard.requestDescription')} *</label>
          <textarea
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            className="input-dark w-full py-2 min-h-[100px]"
            placeholder={t('b2bDashboard.requestDescriptionPlaceholder')}
            data-testid="b2b-request-description"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">{t('b2bDashboard.addressOptional')}</label>
          <input
            value={form.adresse}
            onChange={e => setForm(f => ({ ...f, adresse: e.target.value }))}
            className="input-dark w-full py-2"
            placeholder={t('b2bDashboard.addressPlaceholder')}
            data-testid="b2b-request-adresse"
          />
        </div>
        <button
          onClick={onSubmit}
          disabled={submitting}
          className="w-full py-3 rounded-xl bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/30 font-bold transition-all"
          data-testid="b2b-request-submit"
        >
          <Send className="w-4 h-4 inline mr-2" />
          {submitting ? t('b2bDashboard.sending') : t('b2bDashboard.sendRequest')}
        </button>
      </div>
    )}

    {requests.length === 0 && !showForm ? (
      <div className="text-center py-12 text-gray-400">
        <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
        <p>{t('b2bDashboard.noQuoteRequest')}</p>
        <p className="text-sm mt-2">{t('b2bDashboard.clickNewRequestHint')}</p>
      </div>
    ) : (
      requests.map(req => (
        <div key={req.id} className="card-dark p-4 space-y-2" data-testid={`b2b-request-${req.id}`}>
          <div className="flex items-center justify-between">
            <div className="font-bold text-white">{req.titre || t('b2bDashboard.quoteRequestTitle')}</div>
            <span className={`px-2 py-1 rounded-full text-xs font-bold border ${
              req.status === 'nouveau' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
              req.status === 'en_cours' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
              req.status === 'refuse' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
              'bg-green-500/20 text-green-400 border-green-500/30'
            }`}>
              {req.status === 'nouveau' ? t('b2bDashboard.requestStatus.new') : req.status === 'en_cours' ? t('b2bDashboard.requestStatus.inProgress') : req.status === 'refuse' ? t('b2bDashboard.requestStatus.refused') : t('b2bDashboard.requestStatus.processed')}
            </span>
          </div>
          <p className="text-sm text-gray-300">{req.description}</p>
          {req.adresse && <p className="text-xs text-gray-500"><MapPin className="w-3 h-3 inline mr-1" />{req.adresse}</p>}
          {req.staff_note && (
            <div className="mt-2 p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-xs text-cyan-300">
              <strong>{t('b2bDashboard.teamResponse')}</strong> {req.staff_note}
            </div>
          )}
          {req.motif_refus && (
            <div className="mt-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-300">
              <strong>{t('b2bDashboard.refusalReason')}</strong> {req.motif_refus}
            </div>
          )}
          <div className="text-xs text-gray-600">{req.created_at ? formatDate(req.created_at, { dateStyle: 'short', timeStyle: 'short' }) : ''}</div>
        </div>
      ))
    )}
  </div>
  );
};
